import type { LiteralUnion } from "type-fest"

import { makeFileLogger } from "@/base/logger"

import { translateDeviceReturnCode } from "@/redux/device-return-code"
import {
  getIsOnlineFromState,
  markUserPerceivedOffline,
} from "@/redux/network.reducer"
import { useRootDispatch } from "@/redux/useRootDispatch"
import { useRootSelector } from "@/redux/useRootSelector"
import React from "react"
import { useTranslation } from "react-i18next"

import { useToasts } from "./useToasts"
import { getIsDemoModeActiveFromState } from "@/redux/selectors"

export interface ErrorHandlerOptions {
  toastMessage?: LiteralUnion<"default", string> | boolean
  /**
   * True when the error came from a background poll (e.g. the 10s
   * LoadDeviceActivity refresh) rather than a user-initiated action.
   * Background-poll offline failures are silenced — no toast, no
   * loud-banner trigger — because they shouldn't interrupt the user
   * to tell them the thing they didn't ask for didn't happen.
   */
  isBackgroundPoll?: boolean
}

const logger = makeFileLogger({ fileName: `useErrorHandler.ts` })

import { getDeviceErrorI18nKey } from "./device-error-keys"

/**
 * Module-level throttle for the offline toast. Confirms to the user that
 * the action they just took didn't go through. Background polls pass
 * ``isBackgroundPoll: true`` and skip this path entirely, so the
 * throttle only ever sees user-initiated actions.
 */
const OFFLINE_TOAST_THROTTLE_MS = 10_000
let lastOfflineToastAt = 0

/**
 * True when the error originated from a raw fetch/network failure that
 * should be treated as "phone has no service" rather than a server bug.
 */
function isNetworkOfflineError(error: unknown): boolean {
  const errorName = (error as Partial<Error> | undefined)?.name
  if (errorName === "NetworkOfflineError") return true
  // RN's fetch surfaces offline as a TypeError ("Network request
  // failed"); web fetch uses "Failed to fetch". Catch these in case
  // they originate outside sendRequest (e.g. third-party SDK calls
  // routed through the same error handler).
  if (errorName === "TypeError") {
    const message = (error as Partial<Error> | undefined)?.message ?? ""
    return /network request failed|failed to fetch|load failed/i.test(message)
  }
  return false
}

/**
 * Hook for handling errors on external api calls
 */
export function useErrorHandler(baseOptions?: ErrorHandlerOptions) {
  const { t } = useTranslation("devices")
  const { t: tCommon } = useTranslation()
  const toasts = useToasts()
  const dispatch = useRootDispatch()
  const isDemoEnabled = useRootSelector(getIsDemoModeActiveFromState)
  const isOnline = useRootSelector(getIsOnlineFromState)

  const baseToastMessage = baseOptions?.toastMessage
  const baseIsBackgroundPoll = baseOptions?.isBackgroundPoll
  // const defaultTitle = t("oops", { ns: "common" })
  /**
   *
   */
  return React.useCallback(
    (error: unknown, overrides?: ErrorHandlerOptions): undefined => {
      const errorName = (error as Partial<Error> | undefined)?.name
      // ignore async thunk condition rejection
      if (errorName === "ConditionError") {
        return undefined
      }

      let toastMessage = baseToastMessage ?? overrides?.toastMessage
      const isBackgroundPoll =
        overrides?.isBackgroundPoll ?? baseIsBackgroundPoll ?? false

      // Offline takes priority over every other branch. Background polls
      // skip the user-visible surface entirely — they shouldn't drag the
      // user's attention to a request they didn't initiate. User-initiated
      // failures show a throttled toast AND flip ``userPerceivedOffline``
      // so the loud OFFLINE pill comes up.
      const looksLikeOffline = isNetworkOfflineError(error)
      if (looksLikeOffline || !isOnline) {
        if (isBackgroundPoll) {
          logger.error(JSON.stringify(error))
          return undefined
        }
        dispatch(markUserPerceivedOffline())
        const now = Date.now()
        if (
          !isDemoEnabled
          && now - lastOfflineToastAt > OFFLINE_TOAST_THROTTLE_MS
        ) {
          lastOfflineToastAt = now
          toasts.error(tCommon("offlineErrorMessage"))
        }
        logger.error(JSON.stringify(error))
        return undefined
      }

      if (toastMessage === "default" || toastMessage === true) {
        // Structured device return codes from the backend's lambda
        // wrapper take priority — translateDeviceReturnCode handles
        // both known names (with code-specific CTAs) and unknown
        // future codes (with a generic ``(code N)`` fallback).
        const deviceReturnCode = (
          error as { deviceReturnCode?: number } | undefined
        )?.deviceReturnCode
        const statusCode = (error as { statusCode?: number } | undefined)
          ?.statusCode
        if (typeof deviceReturnCode === "number") {
          toastMessage = translateDeviceReturnCode(deviceReturnCode)
        } else if (statusCode === 453 || statusCode === 461) {
          // Forbidden / FarmUserPermissionsDenied (backend/errors.py).
          // Surface a translated permission message instead of the
          // generic fallback.
          toastMessage = tCommon("permissionDeniedErrorMessage")
        } else {
          const errorMessage = (error as Partial<Error> | undefined)?.message
          const i18nKey = getDeviceErrorI18nKey(errorMessage)
          if (i18nKey) {
            toastMessage = t(i18nKey as never)
          } else {
            toastMessage = tCommon("somethingWentWrong")
          }
        }
      }

      if (
        !isDemoEnabled &&
        typeof toastMessage === "string" &&
        toastMessage.length > 0
      ) {
        toasts.error(toastMessage)
      }

      // log
      logger.error(JSON.stringify(error))
      return undefined
    },
    [
      baseIsBackgroundPoll,
      baseToastMessage,
      dispatch,
      isDemoEnabled,
      isOnline,
      t,
      tCommon,
      toasts,
    ],
  )
}
